import { ApiClient } from '@api/api-configuration';
import { BillingEntityTypeEnum, BillingTransactionTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import { Client } from '@api/READ_ONLY/client_api/Api';
import { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import { VisitPopulated, VisitStatusEnum } from '@api/READ_ONLY/visits_api/Api';
import { DEFAULT_CURRENCY } from '@app/common/constants';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import FormElement from '@app/components/ui/FormElement';
import Text from '@app/components/ui/Text/Text';
import { navigationBypassState, useFormNavigationGuard } from '@app/hooks/useFormNavigationGuard';
import { getDatePickerLocaleByLanguage, getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import { TranslationKeys } from '@app/i18n/translationKeys';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import CaregiversSelectGeneric from '@feat-caregivers/components/CaregiversSelect';
import ClientSelectGeneric from '@feat-clients/components/ClientSelect';
import DoctorSelectGeneric from '@feat-doctors/components/DoctorSelect/DoctorSelectGeneric';
import { useQuery } from '@tanstack/react-query';
import { Button, DatePicker, Input, InputNumber, Select } from 'antd';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FiUsers } from 'react-icons/fi';
import {
  MdOutlineCheckCircle,
  MdOutlineInfo,
  MdOutlinePayments,
  MdOutlinePerson,
  MdOutlineWarningAmber,
} from 'react-icons/md';
import { type PaymentFormValues, type PaymentPayload, type StaffType } from '../../Payments/types';

type ConductPaymentFormProps = {
  initialValues: PaymentFormValues;
  resetSignal?: number;
  isSubmitting?: boolean;
  onSubmit: (payload: PaymentPayload) => void | Promise<void>;
  onSidebarEntityTypeChange?: (entityType: BillingEntityTypeEnum | undefined) => void;
  isClientDisabled?: boolean;
  isPaymentTypeDisabled?: boolean;
  isVisitDisabled?: boolean;
  isStaffTypeDisabled?: boolean;
  isStaffDisabled?: boolean;
  submitLabelKey?: string;
  enableNavigationGuard?: boolean;
};

const staffTypeOptions = [
  { value: 'doctor', labelKey: 'doctor' },
  { value: 'caregiver', labelKey: 'caregiver' },
] as const;

type VisitSelectOption = {
  value: number;
  label: string;
  title: string;
  addressLabel: string;
  dateLabel: string;
  timeLabel: string;
  priceLabel: string;
  selectedLabel: string;
  servicesLabel: string;
};

const MAX_VISIBLE_SERVICES = 2;

const MAX_PAYMENT_AMOUNT = 1_000_000;
const ELIGIBLE_BALANCE_THRESHOLD = 0;

const isClientPayment = (type: BillingTransactionTypeEnum) => type === BillingTransactionTypeEnum.ClientPayment;
const isStaffPayout = (type: BillingTransactionTypeEnum) =>
  type === BillingTransactionTypeEnum.DoctorPayout || type === BillingTransactionTypeEnum.CaregiverPayout;

const formatAbsoluteCurrency = (value: number, locale: string) =>
  currencyFormatter(Math.abs(value), DEFAULT_CURRENCY, locale);

const buildDirtyComparableValues = (values: PaymentFormValues) => ({
  clientId: values.clientId ?? null,
  visitId: values.visitId ?? null,
  staffType: values.staffType ?? null,
  staffId: values.staffId ?? null,
  date: values.date?.toISOString() ?? null,
  amount: values.amount ?? null,
  notes: values.notes ?? '',
});

type EligibilityState = 'pending' | 'loading' | 'eligible' | 'warning' | 'blocked';
type EligibilityCardVariant = 'info' | 'loading' | 'success' | 'warning';

const EligibilityStatusCard = ({
  variant,
  title,
  description,
  balanceText,
  isBalanceProminent = false,
}: {
  variant: EligibilityCardVariant;
  title: string;
  description?: string;
  balanceText?: string;
  isBalanceProminent?: boolean;
}) => {
  const config: Record<
    EligibilityCardVariant,
    {
      wrapperClassName: string;
      iconClassName: string;
      icon: typeof MdOutlineInfo;
    }
  > = {
    info: {
      wrapperClassName: 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-50/70',
      iconClassName: 'border-sky-200 bg-sky-100 text-sky-600',
      icon: MdOutlineInfo,
    },
    loading: {
      wrapperClassName: 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50/80',
      iconClassName: 'border-slate-200 bg-slate-100 text-slate-500',
      icon: MdOutlinePayments,
    },
    success: {
      wrapperClassName: 'border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/70',
      iconClassName: 'border-emerald-200 bg-emerald-100 text-emerald-600',
      icon: MdOutlineCheckCircle,
    },
    warning: {
      wrapperClassName: 'border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-50/70',
      iconClassName: 'border-rose-200 bg-rose-100 text-rose-600',
      icon: MdOutlineWarningAmber,
    },
  };

  const Icon = config[variant].icon;

  return (
    <div className={`rounded-xl border p-4 ${config[variant].wrapperClassName}`}>
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${config[variant].iconClassName}`}
        >
          <Icon className="text-base" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text type="label1" untranslatedText={title} className="text-slate-900" />
          {description && <Text type="body2" untranslatedText={description} color="#64748b" className="leading-6" />}
          {balanceText && (
            <Text
              type={isBalanceProminent ? 'subtitle2' : 'label2'}
              untranslatedText={balanceText}
              className="text-slate-800"
              fw={isBalanceProminent ? 700 : 600}
              mt={isBalanceProminent ? 6 : 2}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const buildPayload = (values: PaymentFormValues): PaymentPayload | null => {
  if (!values.date || values.amount === undefined) {
    return null;
  }

  const date = values.date.toISOString();

  if (isClientPayment(values.paymentType)) {
    if (!values.clientId) return null;

    return {
      type: BillingTransactionTypeEnum.ClientPayment,
      clientId: values.clientId,
      visitId: values.visitId,
      date,
      amount: values.amount,
      notes: values.notes,
    };
  }

  if (!values.staffId) {
    return null;
  }

  return {
    type: values.paymentType as BillingTransactionTypeEnum.DoctorPayout | BillingTransactionTypeEnum.CaregiverPayout,
    staffId: values.staffId,
    date,
    amount: values.amount,
    notes: values.notes,
  };
};

const ConductPaymentForm = ({
  initialValues,
  resetSignal = 0,
  onSubmit,
  onSidebarEntityTypeChange,
  isSubmitting = false,
  isClientDisabled = false,
  isPaymentTypeDisabled = false,
  isVisitDisabled = false,
  isStaffTypeDisabled = false,
  isStaffDisabled = false,
  submitLabelKey = 'submit-payment',
  enableNavigationGuard = true,
}: ConductPaymentFormProps) => {
  const { t, i18n } = useTranslation();
  const [selectorResetVersion, setSelectorResetVersion] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    defaultValues: initialValues,
  });

  const initialValuesKey = useMemo(
    () =>
      JSON.stringify({
        paymentType: initialValues.paymentType,
        clientId: initialValues.clientId ?? null,
        visitId: initialValues.visitId ?? null,
        staffType: initialValues.staffType ?? null,
        staffId: initialValues.staffId ?? null,
        date: initialValues.date?.toISOString() ?? null,
        amount: initialValues.amount ?? null,
        notes: initialValues.notes ?? '',
      }),
    [
      initialValues.amount,
      initialValues.clientId,
      initialValues.date,
      initialValues.notes,
      initialValues.paymentType,
      initialValues.staffId,
      initialValues.staffType,
      initialValues.visitId,
    ]
  );
  const lastInitialValuesKeyRef = useRef(initialValuesKey);
  const baselineValuesRef = useRef(buildDirtyComparableValues(initialValues));
  const lastResetSignalRef = useRef(resetSignal);

  useEffect(() => {
    const didInitialValuesChange = lastInitialValuesKeyRef.current !== initialValuesKey;
    const didResetSignalChange = lastResetSignalRef.current !== resetSignal;

    if (!didInitialValuesChange && !didResetSignalChange) {
      return;
    }

    lastInitialValuesKeyRef.current = initialValuesKey;
    lastResetSignalRef.current = resetSignal;
    baselineValuesRef.current = buildDirtyComparableValues(initialValues);
    reset(initialValues);
  }, [initialValues, initialValuesKey, reset, resetSignal]);

  const watchedValues = watch();
  const isDirtyExcludingPaymentType = useMemo(() => {
    const currentComparableValues = buildDirtyComparableValues(watchedValues);
    return JSON.stringify(currentComparableValues) !== JSON.stringify(baselineValuesRef.current);
  }, [watchedValues]);

  useFormNavigationGuard({
    isDirty: isDirtyExcludingPaymentType,
    enabled: enableNavigationGuard,
  });

  const paymentType = watch('paymentType');
  const staffType = watch('staffType');
  const staffId = watch('staffId');
  const clientId = watch('clientId');

  useEffect(() => {
    if (!onSidebarEntityTypeChange) {
      return;
    }

    if (isClientPayment(paymentType)) {
      onSidebarEntityTypeChange(BillingEntityTypeEnum.Client);
      return;
    }

    if (staffType === 'doctor') {
      onSidebarEntityTypeChange(BillingEntityTypeEnum.Doctor);
      return;
    }

    if (staffType === 'caregiver') {
      onSidebarEntityTypeChange(BillingEntityTypeEnum.Caregiver);
      return;
    }

    onSidebarEntityTypeChange(undefined);
  }, [onSidebarEntityTypeChange, paymentType, staffType]);

  const resolvedClientId = isClientPayment(paymentType) ? clientId : undefined;
  const resolvedDoctorId = staffType === 'doctor' ? staffId : undefined;
  const resolvedCaregiverId = staffType === 'caregiver' ? staffId : undefined;
  const numberLocale = getNumberFormattingLocaleByLanguage(i18n.language);

  const clientQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CLIENT, resolvedClientId],
    enabled: Boolean(resolvedClientId),
    queryFn: async (): Promise<Client | null> => {
      if (!resolvedClientId) {
        return null;
      }

      const response = await ApiClient.clientApi.clients.getClientById(resolvedClientId);
      return response.data ?? null;
    },
  });

  const doctorQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_DOCTOR, resolvedDoctorId],
    enabled: Boolean(resolvedDoctorId),
    queryFn: async (): Promise<Doctor | null> => {
      if (!resolvedDoctorId) {
        return null;
      }

      const response = await ApiClient.doctorsApi.doctors.getDoctorById(resolvedDoctorId);
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const caregiverQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CAREGIVER, resolvedCaregiverId],
    enabled: Boolean(resolvedCaregiverId),
    queryFn: async (): Promise<CaregiverMinified | null> => {
      if (!resolvedCaregiverId) {
        return null;
      }

      const response = await ApiClient.caregiverApi.caregivers.getCaregiverById(resolvedCaregiverId);
      return (response.data as CaregiverMinified) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const clientVisitsQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CLIENT_VISITS, resolvedClientId],
    enabled: Boolean(resolvedClientId) && !isVisitDisabled,
    queryFn: async (): Promise<VisitPopulated[]> => {
      if (!resolvedClientId) {
        return [];
      }

      const response = await ApiClient.visitsApi.visits.listVisits({
        client_id: resolvedClientId,
        visit_status: VisitStatusEnum.COMPLETED,
        sort: '-start_time',
      });

      return response.data.data ?? [];
    },
  });

  const balanceQueryInput =
    paymentType === BillingTransactionTypeEnum.ClientPayment && resolvedClientId
      ? {
          entityType: BillingEntityTypeEnum.Client,
          entityId: resolvedClientId,
        }
      : paymentType === BillingTransactionTypeEnum.DoctorPayout && resolvedDoctorId
        ? {
            entityType: BillingEntityTypeEnum.Doctor,
            entityId: resolvedDoctorId,
          }
        : paymentType === BillingTransactionTypeEnum.CaregiverPayout && resolvedCaregiverId
          ? {
              entityType: BillingEntityTypeEnum.Caregiver,
              entityId: resolvedCaregiverId,
            }
          : null;

  const entityBalanceQuery = useQuery({
    queryKey: [
      BILLING_QUERY_KEYS.PAYMENT_FORM_ENTITY_BALANCE,
      balanceQueryInput?.entityType,
      balanceQueryInput?.entityId,
    ],
    enabled: Boolean(balanceQueryInput),
    queryFn: async () => {
      if (!balanceQueryInput) {
        return null;
      }

      const response = await ApiClient.billingApi.billing.getEntityBalance(
        balanceQueryInput.entityType,
        balanceQueryInput.entityId
      );
      return response.data ?? null;
    },
    staleTime: 0,
  });

  const selectedClient = resolvedClientId ? (clientQuery.data ?? undefined) : undefined;
  const selectedDoctor = resolvedDoctorId ? (doctorQuery.data ?? undefined) : undefined;
  const selectedCaregiver = resolvedCaregiverId ? (caregiverQuery.data ?? undefined) : undefined;

  const requiredMessage = (fieldKey: string) => t('field-required', { field: t(fieldKey) });

  const maxPaymentAmountMessage = t('max-payment-amount', {
    max: new Intl.NumberFormat(i18n.language).format(MAX_PAYMENT_AMOUNT),
  });
  const minPaymentAmountMessage = t('min-payment-amount');

  const dateFormat = t('date-format');
  const datePickerLocale = getDatePickerLocaleByLanguage(i18n.language);
  const isBalanceLoading =
    Boolean(balanceQueryInput) && (entityBalanceQuery.isLoading || entityBalanceQuery.isFetching);
  const entityBalanceAmount = entityBalanceQuery.data?.balance;
  const hasEntityBalance = typeof entityBalanceAmount === 'number';
  const hasNonPositiveClientBalance =
    isClientPayment(paymentType) && hasEntityBalance && entityBalanceAmount <= ELIGIBLE_BALANCE_THRESHOLD;
  const hasNonPositiveStaffBalance =
    isStaffPayout(paymentType) && hasEntityBalance && entityBalanceAmount <= ELIGIBLE_BALANCE_THRESHOLD;
  const isEntityEligibleForPayment = hasEntityBalance
    ? isClientPayment(paymentType)
      ? entityBalanceAmount > ELIGIBLE_BALANCE_THRESHOLD
      : entityBalanceAmount > ELIGIBLE_BALANCE_THRESHOLD
    : false;
  const eligibilityState: EligibilityState = !balanceQueryInput
    ? 'pending'
    : isBalanceLoading
      ? 'loading'
      : isEntityEligibleForPayment
        ? 'eligible'
        : hasNonPositiveClientBalance || hasNonPositiveStaffBalance
          ? 'warning'
          : 'blocked';
  const canSubmitPayment = eligibilityState === 'eligible' || eligibilityState === 'warning';
  const selectedEntityLabel =
    paymentType === BillingTransactionTypeEnum.ClientPayment
      ? t('client')
      : staffType === 'caregiver'
        ? t('caregiver')
        : t('doctor');
  const eligibilityContextKey = isClientPayment(paymentType) ? 'client' : 'staff';
  const eligibilityViewKey = [
    eligibilityContextKey,
    balanceQueryInput?.entityType ?? 'none',
    balanceQueryInput?.entityId ?? 'none',
  ].join('-');
  const currentBalanceText = balanceQueryInput
    ? hasEntityBalance
      ? isClientPayment(paymentType)
        ? entityBalanceAmount > 0
          ? `${t('amount-due')}: ${formatAbsoluteCurrency(entityBalanceAmount, numberLocale)}`
          : entityBalanceAmount < 0
            ? `${t('prepaid')}: ${formatAbsoluteCurrency(entityBalanceAmount, numberLocale)}`
            : `${t('balance')}: ${currencyFormatter(0, DEFAULT_CURRENCY, numberLocale)}`
        : entityBalanceAmount > 0
          ? `${t('amount-due')}: ${formatAbsoluteCurrency(entityBalanceAmount, numberLocale)}`
          : entityBalanceAmount < 0
            ? `${t('overpaid')}: ${formatAbsoluteCurrency(entityBalanceAmount, numberLocale)}`
            : `${t('balance')}: ${currencyFormatter(0, DEFAULT_CURRENCY, numberLocale)}`
      : '-'
    : '-';

  const staffTypeSelectOptions = staffTypeOptions.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

  const visitSelectOptions: VisitSelectOption[] = (clientVisitsQuery.data ?? []).map((visit) => {
    const dateLabel = dayjs(visit.startTime).isValid() ? dayjs(visit.startTime).format(dateFormat) : visit.startTime;
    const startTimeLabel = dayjs(visit.startTime).isValid() ? dayjs(visit.startTime).format('HH:mm') : '';
    const endTimeLabel = dayjs(visit.endTime).isValid() ? dayjs(visit.endTime).format('HH:mm') : '';
    const timeLabel = startTimeLabel && endTimeLabel ? `${startTimeLabel}-${endTimeLabel}` : '';
    const street = visit.street?.trim() ?? '';
    const city = visit.city?.trim() ?? '';
    const postalCode = visit.postalCode?.trim() ?? '';
    const addressBase = [street, city].filter(Boolean).join(', ');

    let addressLabel = addressBase;
    if (addressBase && postalCode) {
      addressLabel = `${addressBase} ${postalCode}`;
    } else if (!addressBase && postalCode) {
      addressLabel = postalCode;
    }

    if (!addressLabel) {
      addressLabel = `${t('visit')} #${visit.id}`;
    }

    const serviceNames = (visit.services ?? []).map((service) => service.service?.name?.trim() ?? '').filter(Boolean);
    const visibleServiceNames = serviceNames.slice(0, MAX_VISIBLE_SERVICES).join(', ');
    const remainingServicesCount = serviceNames.length - MAX_VISIBLE_SERVICES;
    const servicesLabel =
      remainingServicesCount > 0 ? `${visibleServiceNames} +${remainingServicesCount}` : visibleServiceNames;

    const metadataLabel = [dateLabel, timeLabel].filter(Boolean).join(' • ');
    const selectedLabel = [addressLabel, metadataLabel].filter(Boolean).join(' • ');
    const priceLabel =
      typeof visit.priceTotal === 'number'
        ? currencyFormatter(Math.abs(visit.priceTotal), DEFAULT_CURRENCY, numberLocale)
        : '';

    return {
      value: visit.id,
      label: [addressLabel, metadataLabel, servicesLabel].filter(Boolean).join(' '),
      title: '',
      addressLabel,
      dateLabel,
      timeLabel,
      priceLabel,
      selectedLabel,
      servicesLabel,
    };
  });

  const handleValidSubmit = async (values: PaymentFormValues) => {
    if (!canSubmitPayment) {
      return;
    }

    const payload = buildPayload(values);
    if (!payload) return;
    await Promise.resolve(onSubmit(payload));

    const nextBaselineValues: PaymentFormValues = {
      paymentType: values.paymentType,
      clientId: undefined,
      visitId: undefined,
      staffType: isStaffPayout(values.paymentType) ? values.staffType : undefined,
      staffId: undefined,
      date: dayjs(),
      amount: undefined,
      notes: '',
    };

    baselineValuesRef.current = buildDirtyComparableValues(nextBaselineValues);
    reset(nextBaselineValues);
    navigationBypassState.allow = true;
    setSelectorResetVersion((previous) => previous + 1);
  };

  const handlePaymentTypeChange = (
    nextType: BillingTransactionTypeEnum,
    onChange: (value: BillingTransactionTypeEnum) => void
  ) => {
    const nextValues: PaymentFormValues = {
      paymentType: nextType,
      clientId: undefined,
      visitId: undefined,
      staffType: isClientPayment(nextType)
        ? undefined
        : nextType === BillingTransactionTypeEnum.CaregiverPayout
          ? 'caregiver'
          : 'doctor',
      staffId: undefined,
      date: dayjs(),
      amount: undefined,
      notes: '',
    };

    onChange(nextType);
    baselineValuesRef.current = buildDirtyComparableValues(nextValues);
    reset(nextValues);
    setSelectorResetVersion((previous) => previous + 1);
    clearErrors(['clientId', 'visitId', 'staffType', 'staffId', 'date', 'amount', 'notes']);
  };

  const handleStaffTypeChange = (nextType: StaffType | undefined, onChange: (value: StaffType | undefined) => void) => {
    onChange(nextType);

    setValue('staffType', nextType, { shouldDirty: true, shouldTouch: true });
    setValue('staffId', undefined, { shouldDirty: false, shouldTouch: true });
    setValue(
      'paymentType',
      nextType === 'doctor'
        ? BillingTransactionTypeEnum.DoctorPayout
        : nextType === 'caregiver'
          ? BillingTransactionTypeEnum.CaregiverPayout
          : BillingTransactionTypeEnum.DoctorPayout,
      { shouldDirty: false, shouldTouch: true }
    );
    clearErrors(['staffId']);
  };

  return (
    <form
      onSubmit={handleSubmit(handleValidSubmit)}
      className="w-full rounded-lg border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 text-slate-600">
            <MdOutlinePayments className="text-lg" />
          </div>

          <Text type="label1" transKey="record-payment" />
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-6">
        <FormElement label={t('payment-type')} required error={errors.paymentType?.message}>
          <Controller
            name="paymentType"
            control={control}
            rules={{ required: requiredMessage('payment-type') }}
            render={({ field }) => (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => handlePaymentTypeChange(BillingTransactionTypeEnum.ClientPayment, field.onChange)}
                  disabled={isPaymentTypeDisabled}
                  className={[
                    'rounded-lg border px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                    isClientPayment(field.value)
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                      : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-white',
                  ].join(' ')}
                >
                  <span className="flex items-center justify-center gap-2">
                    <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md border border-emerald-300 bg-white text-emerald-500">
                      <MdOutlinePerson className="text-sm" />
                    </span>
                    <Text type="label2" transKey="collection-from-client" />
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handlePaymentTypeChange(
                      staffType === 'caregiver'
                        ? BillingTransactionTypeEnum.CaregiverPayout
                        : BillingTransactionTypeEnum.DoctorPayout,
                      field.onChange
                    )
                  }
                  disabled={isPaymentTypeDisabled}
                  className={[
                    'rounded-lg border px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
                    isStaffPayout(field.value)
                      ? 'border-blue-300 bg-blue-50 text-blue-600'
                      : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-white',
                  ].join(' ')}
                >
                  <span className="flex items-center justify-center gap-2">
                    <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md border border-blue-300 bg-white text-blue-500">
                      <FiUsers className="text-[12px]" />
                    </span>
                    <Text type="label2" transKey="payment-to-staff" />
                  </span>
                </button>
              </div>
            )}
          />
        </FormElement>

        {isClientPayment(paymentType) && (
          <FormElement label={t('client')} required error={errors.clientId?.message}>
            <Controller
              name="clientId"
              control={control}
              rules={{ required: requiredMessage('client') }}
              render={({ field }) => (
                <ClientSelectGeneric
                  key={`client-selector-${selectorResetVersion}`}
                  size="large"
                  value={field.value}
                  placeholder={t('select')}
                  disabled={isClientDisabled}
                  selectedItemsProvider={() => selectedClient}
                  onClientChange={(selectedClientValue) => {
                    setValue('visitId', undefined, { shouldDirty: false, shouldTouch: true });

                    if (selectedClientValue && !Array.isArray(selectedClientValue)) {
                      const nextClientId = (selectedClientValue as Client).clientId;
                      field.onChange(nextClientId);
                      setValue('clientId', nextClientId, { shouldDirty: true, shouldTouch: true });
                      clearErrors(['clientId', 'visitId']);
                      return;
                    }

                    field.onChange(undefined);
                    setValue('clientId', undefined, { shouldDirty: true, shouldTouch: true });
                    clearErrors(['visitId']);
                  }}
                />
              )}
            />
          </FormElement>
        )}

        {isStaffPayout(paymentType) && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <FormElement label={t('staff-type')} required error={errors.staffType?.message}>
              <Controller
                name="staffType"
                control={control}
                rules={{ required: requiredMessage('staff-type') }}
                render={({ field }) => (
                  <Select
                    {...field}
                    size="large"
                    className="w-full"
                    placeholder={t('select')}
                    options={staffTypeSelectOptions}
                    value={field.value ?? undefined}
                    disabled={isStaffTypeDisabled}
                    onChange={(value) => handleStaffTypeChange(value, field.onChange)}
                  />
                )}
              />
            </FormElement>

            <FormElement label={t('staff')} required error={errors.staffId?.message}>
              <Controller
                name="staffId"
                control={control}
                rules={{
                  required: staffType ? requiredMessage('staff') : false,
                }}
                render={({ field }) => (
                  <>
                    {staffType === 'doctor' && (
                      <DoctorSelectGeneric
                        key={`doctor-selector-${selectorResetVersion}`}
                        size="large"
                        placeholder={t('select')}
                        value={field.value}
                        selectedItemsProvider={() => selectedDoctor}
                        disabled={isStaffDisabled}
                        onDoctorChange={(doctor) => {
                          if (doctor && !Array.isArray(doctor)) {
                            const nextDoctorId = (doctor as Doctor).doctorId;
                            field.onChange(nextDoctorId);
                            setValue('staffId', nextDoctorId, { shouldDirty: true, shouldTouch: true });
                            clearErrors(['staffId']);
                            return;
                          }

                          field.onChange(undefined);
                          setValue('staffId', undefined, { shouldDirty: true, shouldTouch: true });
                        }}
                      />
                    )}

                    {staffType === 'caregiver' && (
                      <CaregiversSelectGeneric
                        key={`caregiver-selector-${selectorResetVersion}`}
                        size="large"
                        placeholder={t('select')}
                        value={field.value}
                        selectedItemsProvider={() => selectedCaregiver}
                        disabled={isStaffDisabled}
                        onCaregiverChange={(caregiver) => {
                          if (caregiver && !Array.isArray(caregiver)) {
                            const nextCaregiverId = (caregiver as CaregiverMinified).caregiverId;
                            field.onChange(nextCaregiverId);
                            setValue('staffId', nextCaregiverId, { shouldDirty: true, shouldTouch: true });
                            clearErrors(['staffId']);
                            return;
                          }

                          field.onChange(undefined);
                          setValue('staffId', undefined, { shouldDirty: true, shouldTouch: true });
                        }}
                      />
                    )}

                    {!staffType && <Select size="large" className="w-full" placeholder={t('select')} disabled />}
                  </>
                )}
              />
            </FormElement>
          </div>
        )}

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={eligibilityViewKey}
            initial={{ opacity: 0, x: 56 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -56 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="w-full"
          >
            {eligibilityState === 'pending' && (
              <EligibilityStatusCard
                variant="info"
                title={
                  isClientPayment(paymentType)
                    ? t('payment-eligibility-select-client-title')
                    : t('payment-eligibility-select-staff-title')
                }
                description={
                  isClientPayment(paymentType)
                    ? t('payment-eligibility-select-client-description')
                    : t('payment-eligibility-select-staff-description')
                }
              />
            )}

            {eligibilityState === 'loading' && (
              <EligibilityStatusCard
                variant="loading"
                title={t('payment-eligibility-checking-title')}
                description={t('payment-eligibility-checking-description', {
                  entity: selectedEntityLabel.toLowerCase(),
                })}
              />
            )}

            {eligibilityState === 'blocked' && (
              <EligibilityStatusCard
                variant="warning"
                title={t('payment-eligibility-no-payment-needed-title', { entity: selectedEntityLabel })}
                description={
                  hasNonPositiveClientBalance
                    ? entityBalanceAmount && entityBalanceAmount < 0
                      ? t('payment-eligibility-client-prepaid-description')
                      : t('payment-eligibility-client-no-amount-due-description')
                    : hasNonPositiveStaffBalance
                      ? entityBalanceAmount && entityBalanceAmount < 0
                        ? t('payment-eligibility-staff-overpaid-description')
                        : t('payment-eligibility-staff-no-amount-due-description')
                      : t('payment-eligibility-not-eligible-description', {
                          entity: selectedEntityLabel.toLowerCase(),
                        })
                }
                balanceText={currentBalanceText}
              />
            )}

            {(eligibilityState === 'eligible' || eligibilityState === 'warning') && (
              <div className="flex flex-col gap-6">
                <EligibilityStatusCard
                  variant={eligibilityState === 'warning' ? 'warning' : 'success'}
                  title={
                    eligibilityState === 'warning'
                      ? hasNonPositiveClientBalance
                        ? entityBalanceAmount && entityBalanceAmount < 0
                          ? t('payment-eligibility-client-prepaid-title', {
                              entity: selectedEntityLabel,
                            })
                          : t('payment-eligibility-non-negative-client-balance-warning-title', {
                              entity: selectedEntityLabel,
                            })
                        : hasNonPositiveStaffBalance
                          ? entityBalanceAmount && entityBalanceAmount < 0
                            ? t('payment-eligibility-staff-overpaid-title', {
                                entity: selectedEntityLabel,
                              })
                            : t('payment-eligibility-non-positive-staff-balance-warning-title', {
                                entity: selectedEntityLabel,
                              })
                          : t('payment-eligibility-zero-balance-warning-title', { entity: selectedEntityLabel })
                      : t('payment-eligibility-amount-due-title', { entity: selectedEntityLabel })
                  }
                  description={
                    eligibilityState === 'warning'
                      ? hasNonPositiveClientBalance
                        ? entityBalanceAmount && entityBalanceAmount < 0
                          ? t('payment-eligibility-client-prepaid-description')
                          : t('payment-eligibility-non-negative-client-balance-warning-description')
                        : hasNonPositiveStaffBalance
                          ? entityBalanceAmount && entityBalanceAmount < 0
                            ? t('payment-eligibility-staff-overpaid-description')
                            : t('payment-eligibility-non-positive-staff-balance-warning-description')
                          : t('payment-eligibility-zero-balance-warning-description')
                      : t('payment-eligibility-amount-due-description')
                  }
                  balanceText={currentBalanceText}
                  isBalanceProminent
                />

                {isClientPayment(paymentType) && (
                  <FormElement label={t('visit')} error={errors.visitId?.message}>
                    <Controller
                      name="visitId"
                      control={control}
                      render={({ field }) => (
                        <Select<number>
                          size="large"
                          className="w-full"
                          placeholder={resolvedClientId ? t('select') : t('select-client-first')}
                          value={field.value ?? undefined}
                          options={visitSelectOptions}
                          optionLabelProp="selectedLabel"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          optionRender={(option) => (
                            <div className="min-w-0 py-0.5">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 truncate text-sm text-slate-900">
                                    {option.data.addressLabel}
                                  </div>
                                  <div className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-slate-500">
                                    <span className="shrink-0">{option.data.dateLabel}</span>
                                    {option.data.servicesLabel && (
                                      <>
                                        <span className="text-slate-300">•</span>
                                        <span className="min-w-0 flex-1 truncate">{option.data.servicesLabel}</span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                <div className="flex shrink-0 flex-col items-end gap-1">
                                  {option.data.timeLabel && (
                                    <span className="text-xs font-medium text-slate-500">{option.data.timeLabel}</span>
                                  )}
                                  {option.data.priceLabel && (
                                    <span className="text-xs font-semibold text-slate-700">
                                      {option.data.priceLabel}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          disabled={isVisitDisabled || !resolvedClientId}
                          loading={clientVisitsQuery.isLoading}
                          notFoundContent={resolvedClientId ? t('no-visits-found') : null}
                          onChange={(value) => {
                            field.onChange(value ?? undefined);
                            clearErrors(['visitId']);
                          }}
                        />
                      )}
                    />
                  </FormElement>
                )}

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <FormElement label={t('date')} required error={errors.date?.message}>
                    <Controller
                      name="date"
                      control={control}
                      rules={{ required: requiredMessage('date') }}
                      render={({ field }) => (
                        <DatePicker
                          size="large"
                          className="w-full"
                          format={dateFormat}
                          locale={datePickerLocale}
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                      )}
                    />
                  </FormElement>

                  <FormElement label={`${t('amount')} (€)`} required error={errors.amount?.message}>
                    <Controller
                      name="amount"
                      control={control}
                      rules={{
                        required: requiredMessage('amount'),
                        validate: (value) => {
                          if (value === undefined || value === null) {
                            return requiredMessage('amount');
                          }

                          if (value <= 0) {
                            return minPaymentAmountMessage;
                          }

                          if (value > MAX_PAYMENT_AMOUNT) {
                            return maxPaymentAmountMessage;
                          }

                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          size="large"
                          className="!w-full"
                          style={{ width: '100%' }}
                          min={0}
                          max={MAX_PAYMENT_AMOUNT}
                          value={field.value ?? undefined}
                          onChange={(value) => field.onChange(value === null ? undefined : value)}
                        />
                      )}
                    />
                  </FormElement>
                </div>

                <FormElement label={t('notes-optional')} error={errors.notes?.message}>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <Input.TextArea
                        rows={4}
                        className="w-full"
                        placeholder={t('add-notes')}
                        style={{ maxHeight: 160, resize: 'vertical' }}
                        value={field.value ?? ''}
                        onChange={(event) => field.onChange(event.target.value)}
                      />
                    )}
                  />
                </FormElement>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        className="mt-8 w-full"
        loading={isSubmitting}
        disabled={!canSubmitPayment}
      >
        <Text
          type="label2"
          transKey={submitLabelKey as TranslationKeys}
          color={!canSubmitPayment ? '#64748b' : 'white'}
        />
      </Button>
    </form>
  );
};

export default ConductPaymentForm;
